import {
  Block,
  BlockType,
  TimeOfDay,
  uuid,
} from "../types/screenplay-editor.types";
import {
  generateBlockPosition,
  assignInitialPositions,
  comparePositions,
  rebalancePositions,
  generateBlockPositionWithAutoRebalance,
} from "./fractional-indexing";

export {
  generateBlockPosition,
  assignInitialPositions,
  comparePositions,
  rebalancePositions,
  generateBlockPositionWithAutoRebalance,
};

export function getNextBlockType(currentType: BlockType): BlockType {
  switch (currentType) {
    case "SCENE_HEADING":
      return "ACTION";
    case "CHARACTER":
      return "PARENTHETICAL";
    case "PARENTHETICAL":
      return "DIALOGUE";
    case "DIALOGUE":
      return "CHARACTER";
    case "TRANSITION":
      return "SCENE_HEADING";
    default:
      return "ACTION";
  }
}

export function createNewBlock(type: BlockType): Block {
  const newBlock: Block = { id: uuid(), type, text: "" };

  if (type === "SCENE_HEADING") {
    newBlock.sceneType = "INT.";
    newBlock.timeOfDay = "DAY";
    newBlock.sceneNumber = ""; // Only exists for scenes
  } else if (type === "PARENTHETICAL") {
    newBlock.text = "()";
  }

  return newBlock;
}

export function addBlockAfter(
  blocks: Block[],
  currentBlockId: string,
): { newBlocks: Block[]; newBlockId: string } {
  const currentIndex = blocks.findIndex((b) => b.id === currentBlockId);
  if (currentIndex === -1) return { newBlocks: blocks, newBlockId: "" };

  const nextType = getNextBlockType(blocks[currentIndex].type);
  const newBlock = createNewBlock(nextType);

  // 1. Calculate the lexicographical position with automatic self-healing list rebalancing
  const currentBlock = blocks[currentIndex];
  const nextBlock = blocks[currentIndex + 1] || null;

  const { position, rebalancedBlocks } = generateBlockPositionWithAutoRebalance(
    blocks,
    currentBlock.position || null,
    (nextBlock && nextBlock.position) || null,
    newBlock.id,
    12 // Rebalance threshold length
  );
  newBlock.position = position;

  // If the NEW block is a scene, calculate its number based on the current list
  if (nextType === "SCENE_HEADING") {
    newBlock.sceneNumber = generateNextSceneNumber(blocks, currentIndex);
  }

  // 2. If a rebalance was triggered, use the rebalanced blocks directly; otherwise, do a standard insert
  let newBlocks: Block[];
  if (rebalancedBlocks) {
    // The rebalanced block array already contains our newBlock with its optimized position.
    // We copy any extra transient properties (like type and sceneNumber) onto that block inside the rebalanced list.
    newBlocks = rebalancedBlocks.map((b) =>
      b.id === newBlock.id ? { ...newBlock, position: b.position } : b,
    );
  } else {
    newBlocks = [
      ...blocks.slice(0, currentIndex + 1),
      newBlock,
      ...blocks.slice(currentIndex + 1),
    ];
  }

  return { newBlocks, newBlockId: newBlock.id };
}
export function deleteBlock(
  blocks: Block[],
  blockIdToDelete: string,
): { newBlocks: Block[]; nextFocusedId: string | null } {
  if (blocks.length <= 1) {
    return { newBlocks: blocks, nextFocusedId: blocks[0]?.id || null };
  }

  const index = blocks.findIndex((b) => b.id === blockIdToDelete);
  if (index === -1) {
    return { newBlocks: blocks, nextFocusedId: null };
  }

  const newBlocks = [...blocks];
  newBlocks.splice(index, 1);

  const nextFocusedId =
    newBlocks[index]?.id || newBlocks[index - 1]?.id || null;

  return { newBlocks, nextFocusedId };
}

export function updateBlock<K extends keyof Block>(
  blocks: Block[],
  id: string,
  key: K,
  value: Block[K],
): Block[] {
  return blocks.map((b) => (b.id === id ? { ...b, [key]: value } : b));
}

export const generateNextSceneNumber = (
  blocks: Block[],
  currentIndex: number, // The index of the block where Enter was pressed
): string => {
  // Find all scene headings before the current cursor
  const prevScenes = blocks
    .slice(0, currentIndex + 1)
    .filter((b) => b.type === "SCENE_HEADING");

  // 1. If it's the first scene in the script
  if (prevScenes.length === 0) return "1";

  const lastScene = prevScenes[prevScenes.length - 1];
  const lastNum = lastScene.sceneNumber || "1";

  // Match pattern: "1" -> ["1", ""] | "1A" -> ["1", "A"]
  const match = lastNum.match(/^(\d+)([A-Z]*)$/);
  if (!match) return "1";

  const baseNumber = match[1];
  const currentSuffix = match[2];

  // 2. Determine if we are INSERTING or APPENDING
  // Look for any scene headings that exist AFTER the current cursor position
  const scenesAfter = blocks
    .slice(currentIndex + 1)
    .filter((b) => b.type === "SCENE_HEADING");

  if (scenesAfter.length > 0) {
    // --- INSERTION LOGIC (StudioBinder Style) ---
    // If we are between 1 and 2, we take "1" and add/increment a letter
    if (currentSuffix === "") {
      return `${baseNumber}A`;
    } else {
      return `${baseNumber}${incrementChar(currentSuffix)}`;
    }
  }

  // --- APPENDING LOGIC ---
  // If we are at the very end of the script, just increment the number (1 -> 2)
  return String(parseInt(baseNumber) + 1);
};

// Helper to handle alphabet incrementing: A -> B, Z -> AA
function incrementChar(text: string): string {
  if (text === "") return "A";
  const lastChar = text.slice(-1);
  const remaining = text.slice(0, -1);

  if (lastChar === "Z") {
    return incrementChar(remaining) + "A";
  }
  return remaining + String.fromCharCode(lastChar.charCodeAt(0) + 1);
}

export function changeBlockType(
  blocks: Block[],
  id: string,
  newType: BlockType,
) {
  const currentIndex = blocks.findIndex((b) => b.id === id);
  if (currentIndex === -1) return blocks;

  const currentBlock = blocks[currentIndex];
  const newBlock = createNewBlock(newType);

  // --- PERSISTENCE LOGIC ---
  // Keep the old text but handle parenthetical formatting
  if (newType === "PARENTHETICAL") {
    const cleanText = currentBlock.text.replace(/[()]/g, "");
    newBlock.text = `(${cleanText})`;
  } else if (currentBlock.type === "PARENTHETICAL") {
    // Strip parentheses when switching FROM a parenthetical to something else
    newBlock.text = currentBlock.text.replace(/[()]/g, "");
  } else {
    newBlock.text = currentBlock.text;
  }

  if (newType === "SCENE_HEADING") {
    newBlock.sceneNumber = generateNextSceneNumber(blocks, currentIndex);
    // Optional: Auto-uppercase text if switching to a scene heading
    newBlock.text = newBlock.text.toUpperCase();
  }

  return blocks.map((b) => (b.id === id ? { ...newBlock, id: b.id, position: b.position } : b));
}

export function parseScreenplayText(content: string): Block[] {
  const lines = content.split("\n");
  const blocks: Block[] = [];
  let lastBlock: Block | null = null;
  let activeSpeaker: string | null = null;

  for (let i = 0; i < lines.length; i++) {
    let originalLine = lines[i]; // Don't trim yet
    let trimmedLine = lines[i].trim().replace(/\s+/g, " ");
    // 1. STANDARDIZE TEXT
    const leadingSpaces = originalLine.search(/\S/);

    // --- FIX 1: RESET CONTEXT ON EMPTY LINES ---
    // This ensures that after "Another!", the speaker is cleared before the Action starts.
    if (lines[i].trim() === "") {
      activeSpeaker = null;
      continue;
    }

    // 2. PAGE NUMBER FILTER
    const isPageNumber =
      /^\d+$/.test(trimmedLine) ||
      /^(PAGE|pg\.?)\s?\d+$/i.test(trimmedLine) ||
      /^[\d]+\.$/.test(trimmedLine);

    if (trimmedLine.length === 0 || isPageNumber) continue;

    let currentBlockType: BlockType | null = null;
    const isAllUpperCase =
      trimmedLine === trimmedLine.toUpperCase() && /[A-Z]/.test(trimmedLine);

    // 3. IDENTIFY STRUCTURAL BLOCKS
    const sceneHeadingStartRegex =
      /^(?:\d+[A-Z]?\.?\s*)?(INT\.?\/EXT\.?|I\/E|INT|EXT|EST\.)\b/i;
    const isTransition =
      isAllUpperCase &&
      (trimmedLine.endsWith(" TO:") ||
        ["FADE IN:", "FADE OUT.", "CUT TO BLACK."].includes(trimmedLine));

    if (sceneHeadingStartRegex.test(trimmedLine)) {
      currentBlockType = "SCENE_HEADING";
      activeSpeaker = null;
    } else if (isTransition) {
      currentBlockType = "TRANSITION";
      activeSpeaker = null;
    }
    // 4. CHARACTER DETECTION
    else if (isAllUpperCase && !trimmedLine.startsWith("(")) {
      let nextLine = "";
      for (let j = i + 1; j < lines.length; j++) {
        const nextTrimmed = lines[j].trim();
        const nextIsPage =
          /^\d+$/.test(nextTrimmed) ||
          /^(PAGE|pg\.?)\s?\d+$/i.test(nextTrimmed);
        if (nextTrimmed.length > 0 && !nextIsPage) {
          nextLine = nextTrimmed;
          break;
        }
      }

      if (
        (nextLine &&
          (nextLine.startsWith("(") || nextLine !== nextLine.toUpperCase())) ||
        /\s\(.*\)$/.test(trimmedLine)
      ) {
        currentBlockType = "CHARACTER";
        activeSpeaker = trimmedLine;
      } else {
        currentBlockType = "ACTION";
        activeSpeaker = null;
      }
    }
    // 5. PARENTHETICALS
    else if (trimmedLine.startsWith("(") && trimmedLine.endsWith(")")) {
      currentBlockType = "PARENTHETICAL";
    }
    // 6. DIALOGUE (Persistent Context)
    else if (
      activeSpeaker &&
      (lastBlock?.type === "CHARACTER" ||
        lastBlock?.type === "PARENTHETICAL" ||
        lastBlock?.type === "DIALOGUE")
    ) {
      // 1. Check if the previous block of dialogue actually ended.
      // If it ends in a comma, or no punctuation at all, it's almost certainly
      // just the dialogue wrapping to a new line.
      const isLastLineComplete = /[.!?]"?$/.test(lastBlock.text.trim());

      // 2. The Indentation Check (The "Hard" Break)
      // We only allow a switch to Action if the indentation is low AND
      // the previous dialogue sentence felt finished.
      if (
        leadingSpaces < 15 &&
        lastBlock?.type !== "CHARACTER" &&
        isLastLineComplete
      ) {
        currentBlockType = "ACTION";
        activeSpeaker = null;
      } else {
        currentBlockType = "DIALOGUE";
      }
    }
    // 7. DEFAULT ACTION
    else {
      currentBlockType = "ACTION";
      activeSpeaker = null;
    }

    // --- Block Construction & Scene Metadata Extraction ---
    // --- FIX 2: PREVENT IMPROPER MERGING ---
    if (
      lastBlock &&
      lastBlock.type === currentBlockType &&
      (currentBlockType === "ACTION" || currentBlockType === "DIALOGUE") &&
      !(currentBlockType === "DIALOGUE" && !activeSpeaker) // Added this guard
    ) {
      lastBlock.text += " " + trimmedLine;
    } else {
      const newBlock = createNewBlock(currentBlockType);

      if (currentBlockType === "SCENE_HEADING") {
        let workingLine = trimmedLine;

        const sceneNumMatch = workingLine.match(/^(\d+[A-Z]*)\.?\s+/i);

        if (sceneNumMatch) {
          // 1. SAVE AS RAW STRING (e.g., "24A")
          newBlock.sceneNumber = String(sceneNumMatch[1]).toUpperCase();
          // Strip "24A. " from the text so the editor stays clean
          workingLine = workingLine.replace(/^(\d+[A-Z]*)\.?\s+/i, "").trim();
        } else {
          // 2. NO NUMBER FOUND: Set to empty string
          newBlock.sceneNumber = "";
        }

        const typeMatch = workingLine.match(
          /^(INT\.?\/EXT\.?|I\/E|INT|EXT|EST\.)/i,
        );
        if (typeMatch) {
          let sType = typeMatch[0].toUpperCase().replace(/[^A-Z/.]/g, "");
          if (!sType.endsWith(".")) sType += ".";
          newBlock.sceneType = sType as any;
          workingLine = workingLine.substring(typeMatch[0].length).trim();
        }

        const suffixRegex =
          /[.\-\s]+(DAY|NIGHT|CONTINUOUS|LATER|MORNING|EVENING|DUSK|DAWN|MORN|AFT|SUNSET|SUNRISE)(?:\s+.*)*$/i;
        const suffixMatch = workingLine.match(suffixRegex);

        if (suffixMatch) {
          const rawTime = suffixMatch[1].toUpperCase();
          const nightKeys = ["NIGHT", "EVENING", "DUSK", "SUNSET"];
          newBlock.timeOfDay = nightKeys.includes(rawTime) ? "NIGHT" : "DAY";
          workingLine = workingLine.substring(0, suffixMatch.index).trim();
        } else {
          workingLine = workingLine.replace(/\s+\d+(\s+\d+)*$/, "").trim();
          newBlock.timeOfDay = "DAY";
        }

        newBlock.text = workingLine
          .replace(/^[-.\s]+/, "")
          .replace(/[-.\s]+$/, "")
          .toUpperCase();
      } else {
        newBlock.text = trimmedLine;
      }

      blocks.push(newBlock);
      lastBlock = newBlock;
    }
  }

  const finalized = blocks.length > 0 ? blocks : [createNewBlock("SCENE_HEADING")];
  return assignInitialPositions(finalized);
}

export function serializeToSbx(blocks: Block[]): string {
  const typeToDivClass: Record<string, string> = {
    SCENE_HEADING: "divtype0",
    ACTION: "divtype2",
    CHARACTER: "divtype3",
    PARENTHETICAL: "divtype4",
    DIALOGUE: "divtype5",
    TRANSITION: "divtype6",
    GENERAL: "divtype2",
  };

  return blocks
    .map((block) => {
      const divClass = typeToDivClass[block.type] || "divtype2";
      let text = block.text || "";
      let extraAttributes = "";

      if (block.type === "SCENE_HEADING") {
        text =
          `${block.sceneType || "INT."} ${text} - ${block.timeOfDay || "DAY"}`.toUpperCase();
        if (block.sceneNumber) {
          extraAttributes = ` data-scene="${block.sceneNumber}"`;
        }
      } else if (block.type === "CHARACTER" || block.type === "TRANSITION") {
        text = text.toUpperCase();
      }

      return `<div class="${divClass}" id="par${block.id}"${extraAttributes}>${text}</div>`;
    })
    .join("");
}
